import { useEffect, useMemo, useState } from "react";
import { FilterResponse } from "@/api/dto/filters";
import { QuickFilterInterface } from "@common/QuickFilterBar";
import { FilterOption } from "@/components/common/FilterComponent";
import Helpers from "@/helpers/helpers";

export function useQuickFilters<T extends { length: number }>(
  data: T,
  currentFilters: object,
  filterOptions?: FilterOption[],
  savedFilter?: FilterResponse,
  allKey: string = "all",
  allCount: number = data.length,
  additionalFilters?: QuickFilterInterface[],
) {
  const getAllFilter = (): QuickFilterInterface => ({
    displayText: `All (${allCount})`,
    key: allKey,
    value: null,
  });

  const quickFilters: QuickFilterInterface[] = useMemo(() => [
    ...(!savedFilter ? [getAllFilter()] : []),
    ...(additionalFilters ?? []),
  ], [data, allKey, allCount, savedFilter, additionalFilters]);

  const [activeQuickFilter, setActiveQuickFilter] = useState<QuickFilterInterface | null>(quickFilters[0] ?? null);

  useEffect(() => {
    // set quick filters that are applied based on filters
    let activeQuickFilter: QuickFilterInterface | null = null;
    if (Helpers.isEmptyUnknown(currentFilters)) {
      // no filters applied make all quick filter active
      activeQuickFilter = quickFilters.find((quickFilter: QuickFilterInterface) => quickFilter.key === allKey) ?? null;
    } else if (filterOptions) {
      // one filter applied make that quick filter active if available
      for(const [key, value] of Object.entries(currentFilters)) {
        const filterOption = filterOptions.find((option: FilterOption) => option.key === key);

        if(filterOption?.hasQuickFilter) {
          for(const filter of value) {
            if(filter.quickFilter) {
              activeQuickFilter = quickFilters.find((quickFilter: QuickFilterInterface) => {
                return quickFilter.value?.value === filter.value;
              }) ?? null;

              break;
            }
          }

          if (activeQuickFilter) {
            // break loop early since there can only be one active quick filter
            break;
          }
        }
      }
    }
    setActiveQuickFilter(activeQuickFilter);
  }, [quickFilters, currentFilters, filterOptions]);

  const resetAllFilters = (quickFilter: QuickFilterInterface, resetFn: () => void) => {
    if (quickFilter.key === allKey) {
      resetFn();
    }
  };

  return { quickFilters, activeQuickFilter, setActiveQuickFilter, getAllFilter, resetAllFilters };
}
